import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Grid,
  Modal,
  TextField,
  Theme,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import CloseIcon from "@material-ui/icons/Close";
import { fade } from "@material-ui/core/styles/colorManipulator";
import * as React from "react";
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from "@sentry/browser";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { RootCompositeFilterView } from "./CohortFilterList";
import { AnyUserFilter, CompositeUserFilter, RootCompositeUserFilter, Cohort, CohortDestinationInfo } from "./types";
import { CohortsState, DELETE_COHORT, UPSERT_COHORT } from "../reducers/cohorts";
import _ from "lodash";
import { makeRequest } from "../util";
import { useAlert } from "react-alert";
import { CohortDestinationsPanel } from "./CohortDestinationsPanel";
import {CloudDownload} from "@material-ui/icons";
import moment from "moment";
import {useHistory} from "react-router";
import {RefreshIcon} from "../components/icons/themes/windows10";
import {DestinationState} from "../reducers/destinations";

const useCohortStyles = makeStyles((theme: Theme) => ({
  content: {
    display: "flex",
    flex: "1 1 auto",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    flex: "0 1 auto",
    height: 70,
  },
  actions: {
    justifyContent: "flex-end",
  },
  widget: {},
  widgets: {
    overflowY: "auto",
    paddingTop: 1,
    margin: 0,
  },
  deleteCohortButton: {
    color: colors.red[600],
    border: `1px solid ${fade(colors.red[600], 0.5)}`,
    "&:hover": {
      border: `1px solid ${colors.red[600]}`,
      backgroundColor: fade(colors.red[600], theme.palette.action.hoverOpacity),
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  createCohortButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.green[600],
    "&:hover": {
      backgroundColor: colors.green[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.green[600],
      },
    },
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
  bold: {
    fontWeight: 550,
  },
}));

const coerceToCompositeFilter = (filter: AnyUserFilter): CompositeUserFilter => {
  if (filter.type !== 'composite_filter') {
    return {type: 'composite_filter', op: 'and', filters: [filter]};
  } else {
    return filter;
  }
};

const getInitialFilter = (initialCohort: Cohort | null): RootCompositeUserFilter => {
  // The root filter view shows any filter as a double-composite filter (a composite filter with all sub-filters also composite filters),
  // with non-composite filters being a double-composite filter consisting of just a single filter, so convert non-composite filters into
  // equivalent double-composite filters.
  if (!initialCohort) {
    return {type: 'composite_filter', op: 'and', filters: []};
  } else if (initialCohort.filter.type !== 'composite_filter') {
    // The filter is not a composite filter, so create a double-nested filter from it.
    return {type: 'composite_filter', op: 'and', filters: [{type: 'composite_filter', op: 'and', filters: [initialCohort.filter]}]};
  } else if (initialCohort.filter.filters.every(filter => filter.type === 'composite_filter')) {
    // All filters are already composite filters.
    return initialCohort.filter as RootCompositeUserFilter;
  } else if (initialCohort.filter.filters.every(filter => filter.type !== 'composite_filter')) {
    // No filters are composite filters, so nest the composite filter within another composite filter.
    return {type: 'composite_filter', op: 'and', filters: [initialCohort.filter]};
  } else {
    // Some filters are not composite filters, so convert the non-composite filters to composite filters.
    return {
      type: 'composite_filter',
      op: initialCohort.filter.op,
      filters: initialCohort.filter.filters.map(coerceToCompositeFilter),
    };
  }
}

interface CohortEditViewProps {
  onClose: () => void;
  selectedCohort: Cohort | null;
  refreshCohortTimes: () => void;
}

const DownloadCSVButton: React.FC<{
  onDownloadCSV: () => void;
  isExistingCohort: boolean;
  hasChanges: boolean;
  isPending: boolean;
}> = ({onDownloadCSV, isExistingCohort, hasChanges, isPending}) => {
  let tooltipTitle = '';

  if (!isExistingCohort) {
    tooltipTitle = "You must save the cohort before downloading the CSV."
  } else if (hasChanges) {
    tooltipTitle = "You should save your changes before downloading the CSV."
  } else if (isPending) {
    tooltipTitle = "This cohort was recently updated and is pending update.";
  }

  const isDisabled = !!tooltipTitle;

  const handleClick = () => {
    if (!isDisabled) {
      onDownloadCSV();
    }
  };

  const btn = (
      <div
          className={clsx("text-xs flex items-center py-1 px-2 font-semibold rounded cursor-pointer",{
            "text-grey-300": isDisabled,
            "text-navy-600 hover:bg-navy-200 active:bg-navy-700 active:text-white": !isDisabled,
          })}
          color={!!tooltipTitle ? "default": "secondary"}
          onClick={handleClick}
      >
        <CloudDownload className="mr-2" style={{width: 16}}/> Download CSV
      </div>
  );

  if (isDisabled) {
    return (
        <Tooltip title={tooltipTitle}>{btn}</Tooltip>
    )
  } else {
    return btn;
  }
}

export const CohortEditView: React.FC<CohortEditViewProps> = ({onClose, selectedCohort, refreshCohortTimes}) => {
  const classes = useCohortStyles();
  const alert = useAlert();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isExistingCohort, setIsExistingCohort] = useState(!!selectedCohort);
  const [unsavedEdits, setUnsavedEdits] = useState(!isExistingCohort);
  const [cohortName, setCohortName] = useState(!!selectedCohort ? selectedCohort.name : '');
  const [cohortID] = useState(!!selectedCohort ? selectedCohort.id : uuidv4());

  const [enabledDests, setEnabledDests] = useState<CohortDestinationInfo>(!!selectedCohort ? selectedCohort.enabled_dests : {});

  const cohorts = useSelector((state: { cohorts: CohortsState }) => state.cohorts.cohorts);

  const [filterDefinition, setFilterDefinition] = useState(getInitialFilter(selectedCohort));

  const [deleteCohortModalOpen, setDeleteCohortModalOpen] = useState(false);

  const destinationState = useSelector((state: { destinations: DestinationState }) => state.destinations.data);
  const destinationData = Object.values(destinationState);
  const destinations = _.sortBy(destinationData, (dest) => {
          try {
             return dest.name.toLowerCase();
          } catch {
            return dest.name;
          }
        }
  );

  const isCohortDefValid = (cohortDef: Cohort) => {
    if (!cohortDef.name || cohortDef.name.length === 0 || cohortDef.name.trim().length === 0) {
      alert.error("The cohort has no name.");
      return false;
    }

    if (!/[a-zA-Z]/.test(cohortDef.name)) {
      alert.error("Cohorts must have at least one letter in their name.");
      return false;
    }

    if (
      cohorts.find(
        (otherCohort) =>
          otherCohort.name === cohortDef.name &&
          otherCohort.id !== cohortDef.id
      )
    ) {
      alert.error(
        "Unable to save cohort. The cohort has the same name as another cohort."
      );
      return false;
    }

    if (!isFilterValid(cohortDef.filter)) {
      return false;
    }

    return true;
  }

  const isFilterValid = (filter: AnyUserFilter): boolean => {
    if (filter.type === 'prop_filter') {
      if (!filter.prop || filter.prop.length == 0) {
        alert.error(
          "The property for one of the user property filters is unselected."
        );
        return false;
      }
    } else if (filter.type === 'agg_filter') {
      if (!filter.event_def_id) {
        alert.error("The event name for one of the event filters is unselected.");
        return false;
      }

      if (!filter.val && filter.val != 0) {
        alert.error("The value for one of the event filters is unselected.");
        return false;
      }

      if (filter.val < 0) {
        alert.error("The value for one of the event filters is negative.");
        return false;
      }

      if (filter.time_filter) {
        if (!filter.time_filter.num) {
          alert.error("The value for one of the time filters is unselected.");
          return false;
        }

        if (filter.time_filter.num == 0) {
          alert.error("The value for one of the time filters is zero.");
          return false;
        }

        if (filter.time_filter.num < 0) {
          alert.error("The value for one of the time filters is negative.");
          return false;
        }
      }

      if (filter.val < 0) {
        alert.error("The value for one of the event filters is negative.");
        return false;
      }

      if (filter.fn !== 'count') {
        alert.error("The function for one of the event filters is invalid");
      }
    } else {
      return filter.filters.every(isFilterValid);
    }

    return true;
  }

  const triggerCohortRecomputeOnly = () => {
    makeRequest<any>('trigger_cohort_recompute', {
      "cohortId": cohortID
    }).finally(() => {
      refreshCohortTimes();
    })
  }

  const triggerCohortDestResync = (destinationId: string) => {
    makeRequest<any>('trigger_cohort_destination_sync', {
      "cohortId": cohortID,
      "resyncTo": destinationId,
    }).finally(() => {
      refreshCohortTimes();
    })
  }

  const handleSaveCohort = () => {
    const newCohort: Cohort = {
      id: cohortID,
      name: cohortName,
      filter: filterDefinition,
      enabled_dests: enabledDests,
      synced_to: null,
      updated_at: null,
      member_count: null,
      is_pending: true,
    };

    if (!isCohortDefValid(newCohort)) {
      return;
    }

    makeRequest<any>("save_cohort", newCohort)
      .then(() => {
        setIsExistingCohort(true);
        dispatch({type: UPSERT_COHORT, payload: newCohort});
        setUnsavedEdits(false);
        alert.success("Cohort Saved");
        history.replace(`/cohort/${newCohort.id}`)
        triggerCohortRecomputeOnly();
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to save cohort.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  const handleDeleteCohort = () => {
    makeRequest<any>("delete_cohort", { cohort_def_id: cohortID })
      .then(() => {
        history.replace('/cohorts/');
        dispatch({type: DELETE_COHORT, payload: cohortID});
        setUnsavedEdits(false);
        alert.success("Cohort Deleted");
        onClose();
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to delete cohort.");
        console.log(error);
        Sentry.captureException(error);
      });
  };

  const handleToggleDestination = (destID: string) => {
    if (unsavedEdits) {
      alert.error("You have unsaved event definition changes.");
      return;
    }

    const newDestObj: CohortDestinationInfo = {};
    const newState = enabledDests[destID] ? {
      ...enabledDests[destID],
      enabled: !enabledDests[destID].enabled
    } : {
      synced_to: null,
      enabled: true,
      is_syncing: false,
    };
    newDestObj[destID] = newState;

    makeRequest<any>("toggle_cohorts_destination", {
      personas_dest_id: destID,
      cohort_def_id: cohortID,
      state: newState.enabled,
    })
      .then(() => {
        const newEnabledDests = _.extend({}, enabledDests, newDestObj);
        setEnabledDests(newEnabledDests);
        dispatch({type: UPSERT_COHORT, payload: {
          id: cohortID,
          name: cohortName,
          filter: filterDefinition,
          enabled_dests: newEnabledDests
        }});

        triggerCohortDestResync(destID);
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to toggle destination.");
        console.log(error);
        Sentry.captureException(error);
      });
  };

  const onDownloadCSV = () => {
    if (unsavedEdits) {
      alert.error("You have unsaved event definition changes.");
      return;
    }

    makeRequest<any>("get_cohort_email_csv", {cohortID})
      .then((response) => {
        var element = document.createElement('a');
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(response.data));
        element.setAttribute('download', "cohort.csv");

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to download CSV.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  const hasLoadedMembershipCount = isExistingCohort && selectedCohort && selectedCohort.member_count != null && !selectedCohort.is_pending;

  return (
    <>
      <div
        className={clsx(
          "px-5 py-4 items-center truncate flex font-bold text-gray-700", classes.header
        )}
      >
        <Grid
            container
            spacing={0}
            alignItems="flex-start"
            alignContent="flex-start"
            className={clsx("pb-2")}
        >
          <Grid item xs={11} className="justify-between flex">
            {cohortName ?
                <span className="text-2xl text-gray-700 font-bold truncate">{cohortName}</span> :
                <span className="text-2xl text-gray-400 font-bold">Unnamed Cohort</span>
            }
            <DownloadCSVButton onDownloadCSV={onDownloadCSV} hasChanges={unsavedEdits} isExistingCohort={isExistingCohort} isPending={!selectedCohort || selectedCohort.is_pending} />
          </Grid>
          {isExistingCohort && selectedCohort && (
              <Grid item xs={11} className="justify-between flex mt-1">
                {!selectedCohort.is_pending && selectedCohort.synced_to ? (
                    <span className="text-xs text-grey-700">Cohort Updated: {moment(selectedCohort.synced_to).fromNow()}</span>
                ) : (
                    <span className="text-xs text-grey-700 animate-pulse"><RefreshIcon className="animate-spin mr-1"/>Cohort Pending Update</span>
                )}
                <span className={clsx("text-xs text-grey-500 pr-2", { "animate-pulse": !hasLoadedMembershipCount })}>Cohort Members: {hasLoadedMembershipCount ? selectedCohort.member_count : "..."}</span>
              </Grid>
          )}
        </Grid>
      </div>
      <div className={classes.content}>
        <Grid
          container
          spacing={0}
          alignItems="flex-start"
          alignContent="flex-start"
          className={clsx(classes.widgets, "px-5 pb-2")}
        >
          <Grid item xs={11}>
            <div className={classes.widget}>
              <Card>
                <CardHeader
                  title={isExistingCohort ? "Edit Audience" : "Add Audience"}
                  classes={{ action: clsx(classes.closeIconAction) }}
                  action={
                    <Button variant="outlined" onClick={onClose}>
                      Back
                    </Button>
                  }
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={4}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        autoComplete="off"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        label="Audience Name"
                        name="name"
                        onChange={(e) => {
                          setCohortName(e.target.value);
                          setUnsavedEdits(true);
                        }}
                        value={cohortName}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
                <Divider />
                <CardContent>
                  <RootCompositeFilterView
                    filter={filterDefinition}
                    onUpdateFilter={(filter) => {
                      setFilterDefinition(filter);
                      setUnsavedEdits(true);
                    }}
                  />
                </CardContent>
                <Divider />
                <CardActions className={classes.actions}>
                  {isExistingCohort &&
                    <Button className={clsx(classes.deleteCohortButton)} onClick={() => setDeleteCohortModalOpen(true)}>DELETE</Button>
                  }
                  <Button
                    className={clsx(classes.createCohortButton)}
                    onClick={handleSaveCohort}
                    variant="outlined"
                  >
                    SAVE
                  </Button>
                </CardActions>
              </Card>
            </div>
          </Grid>
          <Grid item xs={11}>
            <div className={classes.widget}>
              <CohortDestinationsPanel
                destinations={destinations}
                enabledDests={enabledDests}
                onToggleDestination={handleToggleDestination}
                triggerCohortDestResync={triggerCohortDestResync}
              />
            </div>
          </Grid>
        </Grid>
      </div>
      <DeleteCohortModal
        open={deleteCohortModalOpen}
        onClose={() => setDeleteCohortModalOpen(false)}
        onConfirm={handleDeleteCohort}
      />
    </>
  );
};

const useModalStyles = makeStyles((theme: Theme) => ({
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    boxShadow: theme.shadows[20],
    width: 500,
    maxHeight: "100%",
    overflowY: "auto",
    maxWidth: "100%",
  },
  actions: {
    justifyContent: "flex-end",
  },
  deactivateButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.red[600],
    "&:hover": {
      backgroundColor: colors.red[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.red[600],
      },
    },
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
  bold: {
    fontWeight: 550,
  },
}));

const DeleteCohortModal: React.FC<{open: boolean, onClose: () => void, onConfirm: () => void}> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={"Delete Cohort"}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            This is going to affect any Destinations you are sending this cohort
            to, but will not delete any data from Freshpaint.
            <br />
            <br />
            <span className={clsx(classes.bold)}>
              Are you sure you want to delete this cohort?
            </span>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>CANCEL</Button>
          <Button
            className={clsx(classes.deactivateButton)}
            onClick={() => {
              onConfirm();
              onClose();
            }}
            variant="contained"
          >
            DELETE COHORT
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}
